import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';

import { EmptyTab } from '@/components/EmptyTab';
import { InboxListError, getInboxListErrorType } from '@/components/InboxListError';
import { InquiryCardErrorBoundary } from '@/components/InquiryCardErrorBoundary';
import { TabRootFlatList, TabRootListFallback } from '@/components/TabRootList';
import { InboxAnnouncementBanner } from '@/components/inbox/InboxAnnouncementBanner';
import { InboxRowShell } from '@/components/inbox/InboxRowShell';
import { InquiryCard } from '@/components/inbox/InquiryCard';
import { MessageCardSkeleton } from '@/components/inbox/MessageCardSkeleton';
import { QuestionGroupCard } from '@/components/inbox/QuestionGroupCard';
import { LOAD_MORE_SCROLL_THRESHOLD } from '@/components/inbox/constants';
import { type SceneProps } from '@/components/inbox/types';
import { SupportThreadCard } from '@/components/supportThread/SupportThreadCard';
import { Divider } from '@/components/ui/divider';
import { FlatList } from '@/components/ui/flat-list';
import { RefreshControl } from '@/components/ui/refresh-control';
import { Spinner } from '@/components/ui/spinner';
import { VStack } from '@/components/ui/vstack';
import { INBOX_ANNOUNCEMENT_BANNER } from '@/constants/inboxFeatureBanners';
import { useClosedTabAutoPagination } from '@/hooks/inbox/useClosedTabAutoPagination';
import {
  useAnonymousInquiryGroupingEnabled,
  useGroupedInquiriesEnabled,
} from '@/hooks/inbox/useGroupedInquiriesEnabled';
import { useInboxAnnouncementBanner } from '@/hooks/inbox/useInboxAnnouncementBanner';
import { useInboxGrouping } from '@/hooks/inbox/useInboxGrouping';
import {
  useInboxQuestionGroupCardsEnabled,
  useInboxQuestionGroups,
} from '@/hooks/inbox/useInboxQuestionGroups';
import { useInquiryGroupEnrichment } from '@/hooks/inbox/useInquiryGroupEnrichment';
import { getClosedInquiriesV2QueryOptions, type InquiryV2 } from '@/services/getInquiriesV2';
import { getInquiryCancerTypesQueryOptions } from '@/services/getInquiryCancerTypes';
import { userIdAtom } from '@/services/globalState';
import { shouldFetchUserScoped } from '@/util/authTokens';
import { getCancerFocusId } from '@/util/getCancerFocusId';
import {
  buildInboxRows,
  getFirstMultiThreadQuestionGroupKey,
  type InboxRow,
} from '@/util/inboxRows';
import {
  buildInboxInquiryGroups,
  filterClosedTabInquiriesForUser,
  getFirstTabConversationId,
  getInquiryConversationIds,
  type InquiryGroup,
} from '@/util/inquiryGroups';
import { Sentry } from '@/util/sentry';

export function MobileClosedTab({ route }: SceneProps) {
  const selectedCancerFocus = route?.params?.selectedCancerFocus;

  const userId = useAtomValue(userIdAtom);

  const inboxGrouping = useInboxGrouping();

  const isInboxGroupedInquiriesEnabled = useGroupedInquiriesEnabled();

  const isAnonymousInquiryGroupingEnabled = useAnonymousInquiryGroupingEnabled();

  const [refreshing, setRefreshing] = useState(false);

  const { data: inquiryCancerTypesData } = useQuery({
    ...getInquiryCancerTypesQueryOptions(userId),
    enabled: shouldFetchUserScoped(userId),
  });

  const selectedCancerFocusId = getCancerFocusId(selectedCancerFocus, inquiryCancerTypesData);

  const closedQueryEnabled = !!userId;

  const {
    data,
    isLoading,
    isFetching,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    ...getClosedInquiriesV2QueryOptions(userId, selectedCancerFocusId, inboxGrouping),
    enabled: closedQueryEnabled,
  });

  const closedInquiries = useMemo<InquiryV2[]>(
    () =>
      filterClosedTabInquiriesForUser(
        (data?.pages?.flatMap((page) => page?.inquiries ?? []) ?? []) as InquiryV2[],
        userId
      ),
    [data?.pages, userId]
  );

  const isCardList = useInboxQuestionGroupCardsEnabled();

  const { questionGroups, remaining } = useInboxQuestionGroups(closedInquiries, data?.pages);

  const groupedInquiries = useMemo<InquiryGroup[]>(() => {
    return buildInboxInquiryGroups(
      remaining,
      isInboxGroupedInquiriesEnabled,
      userId,
      isAnonymousInquiryGroupingEnabled
    );
  }, [remaining, isInboxGroupedInquiriesEnabled, userId, isAnonymousInquiryGroupingEnabled]);

  const {
    getFullGroupInquiries,
    getGroupExperts,
    getGroupStateBreakdown,
    getGroupLabels,
    getGroupRouteLabels,
  } = useInquiryGroupEnrichment({
    enabled: isInboxGroupedInquiriesEnabled,
    cancerTypeId: selectedCancerFocusId,
    isAnonymousInquiryGroupingEnabled,
    pages: data?.pages,
  });

  const rows = useMemo(
    () => buildInboxRows(groupedInquiries, data?.pages ?? [], questionGroups),
    [groupedInquiries, data?.pages, questionGroups]
  );

  const tipQuestionGroupKey = useMemo(() => getFirstMultiThreadQuestionGroupKey(rows), [rows]);

  useClosedTabAutoPagination({
    enabled: closedQueryEnabled,
    visibleCount: closedInquiries.length,
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
  });

  const { isVisible: isAnnouncementBannerVisible, dismiss: dismissAnnouncementBanner } =
    useInboxAnnouncementBanner({
      enabled: isInboxGroupedInquiriesEnabled,
      userId,
      bannerConfig: INBOX_ANNOUNCEMENT_BANNER,
    });

  const shouldShowAnnouncementBanner = isAnnouncementBannerVisible;

  if (isLoading) {
    return (
      <TabRootListFallback>
        <MessageCardSkeleton />
      </TabRootListFallback>
    );
  }

  if (error) {
    return (
      <TabRootListFallback>
        <InboxListError errorType={getInboxListErrorType(error)} onRetry={() => refetch()} />
      </TabRootListFallback>
    );
  }

  if (!rows.length) {
    return (
      <TabRootListFallback>
        <EmptyTab
          tabKey="closed"
          tabSelectedKey="closed"
          animation="GAIN_CLARITY"
          title="Nothing to see here!"
          description="Once you close an inquiry, you'll be able to reference it here."
        />
      </TabRootListFallback>
    );
  }

  return (
    <TabRootFlatList
      className="flex-1"
      showsVerticalScrollIndicator={false}
      data={rows}
      keyExtractor={(row: InboxRow) => row.key}
      renderItem={({ item: row }: { item: InboxRow }) => {
        if (row.kind === 'supportThread') {
          return (
            <InboxRowShell>
              <SupportThreadCard entry={row.entry} currentTab="closed" />
            </InboxRowShell>
          );
        }

        if (row.kind === 'questionGroup') {
          return (
            <QuestionGroupCard
              questionGroup={row.questionGroup}
              currentTab="closed"
              showTip={row.key === tipQuestionGroupKey}
            />
          );
        }

        const inquiryGroup = row.group;

        const groupLeadInquiry = inquiryGroup.inquiries[0];

        const hasUnread = isInboxGroupedInquiriesEnabled
          ? inquiryGroup.unreadCount > 0
          : groupLeadInquiry.hasUnread;

        const firstTabConversationId =
          isInboxGroupedInquiriesEnabled && inquiryGroup.inquiries.length > 1
            ? (getFirstTabConversationId(inquiryGroup) ?? groupLeadInquiry.conversationId)
            : groupLeadInquiry.conversationId;

        const fullGroupInquiries = getFullGroupInquiries(inquiryGroup);

        const inquiryGroupCount = isInboxGroupedInquiriesEnabled
          ? (groupLeadInquiry.inquiryGroupSize ?? fullGroupInquiries.length)
          : undefined;

        const inquiryGroupExperts = isInboxGroupedInquiriesEnabled
          ? getGroupExperts(inquiryGroup)
          : undefined;

        const inquiryGroupStateBreakdown = isInboxGroupedInquiriesEnabled
          ? getGroupStateBreakdown(inquiryGroup)
          : undefined;

        const routeLabels = isInboxGroupedInquiriesEnabled
          ? getGroupRouteLabels(inquiryGroup)
          : undefined;

        return (
          <InboxRowShell>
            <InquiryCardErrorBoundary conversationId={firstTabConversationId}>
              <InquiryCard
                {...groupLeadInquiry}
                conversationId={firstTabConversationId}
                hasUnread={hasUnread}
                labels={getGroupLabels(inquiryGroup)}
                currentTab="closed"
                groupedInquiriesEnabled={isInboxGroupedInquiriesEnabled}
                groupConversationIds={getInquiryConversationIds(fullGroupInquiries)}
                inquiryGroupCount={inquiryGroupCount}
                inquiryGroupVisibleCount={inquiryGroup.inquiries.length}
                inquiryGroupStateBreakdown={inquiryGroupStateBreakdown}
                groupExperts={inquiryGroupExperts}
                routeLabels={routeLabels}
              />
            </InquiryCardErrorBoundary>
          </InboxRowShell>
        );
      }}
      ListHeaderComponent={
        <>
          {shouldShowAnnouncementBanner ? (
            <VStack className="border-b border-outline-100">
              <InboxAnnouncementBanner
                onDismiss={() => dismissAnnouncementBanner().catch(() => undefined)}
              />
            </VStack>
          ) : null}
        </>
      }
      ItemSeparatorComponent={isCardList ? null : Divider}
      refreshControl={
        !isWeb ? (
          <RefreshControl
            refreshing={refreshing}
            onRefresh={async () => {
              setRefreshing(true);

              try {
                await refetch();
              } catch (e) {
                Sentry.captureException(e, {
                  tags: { component: 'MobileClosedTab', action: 'pull-to-refresh' },
                });
              } finally {
                setRefreshing(false);
              }
            }}
          />
        ) : undefined
      }
      onEndReached={() => {
        if (hasNextPage && !isFetchingNextPage) {
          fetchNextPage().catch(() => undefined);
        }
      }}
      onEndReachedThreshold={LOAD_MORE_SCROLL_THRESHOLD}
      ListFooterComponent={
        <VStack className="w-full items-center py-4">
          {isFetchingNextPage ? <Spinner size="large" /> : null}
        </VStack>
      }
    />
  );
}
